import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { prospects } from '../../api';
import { ENTITY_TYPES, SERVICES, errMsg } from './constants';

const EMPTY = { name: '', entityType: 'Pty Ltd', contact: '', email: '', phone: '', source: '', services: ['Bookkeeping'], monthlyFee: '', onceOffFee: '', notes: '' };

export const ProspectDialog = ({ open, onOpenChange, prospect, onSaved, toast }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(prospect ? { ...EMPTY, ...prospect } : EMPTY); }, [open, prospect]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (s) => setF({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] });

  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, monthlyFee: Number(f.monthlyFee) || 0, onceOffFee: Number(f.onceOffFee) || 0 };
    ['id', 'firmId', 'stage', 'token', 'documents', 'fica', 'info', 'history', 'progress', 'ready', 'createdAt', 'updatedAt', 'stageChangedAt', 'createdBy', 'lostReason', 'convertedClientId', 'convertedAt']
      .forEach((k) => delete body[k]);
    (prospect ? prospects.update(prospect.id, body) : prospects.create(body))
      .then((p) => { toast({ title: prospect ? 'Prospect updated' : 'Prospect added', description: p.name }); onOpenChange(false); onSaved(p); })
      .catch((err) => toast({ title: 'Could not save', description: errMsg(err), variant: 'destructive' }))
      .finally(() => setBusy(false));
  };

  const inp = (k, label, type = 'text', props = {}) => (
    <div>
      <Label htmlFor={`pr-${k}`} className="text-xs">{label}</Label>
      <Input id={`pr-${k}`} type={type} value={f[k] ?? ''} onChange={set(k)} className="mt-1 h-9" data-testid={`prospect-${k}-input`} {...props}/>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prospect-dialog">
        <DialogHeader><DialogTitle>{prospect ? 'Edit prospect' : 'New prospect'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">{inp('name', 'Business / prospect name', 'text', { required: true, placeholder: 'e.g. Zulu Tech Solutions' })}</div>
            <div>
              <Label className="text-xs">Entity type</Label>
              <Select value={f.entityType} onValueChange={(v) => setF({ ...f, entityType: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="prospect-entity-select"><SelectValue/></SelectTrigger>
                <SelectContent>{ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {inp('source', 'Lead source', 'text', { placeholder: 'Referral, website, LinkedIn…' })}
            {inp('contact', 'Contact person')}
            {inp('phone', 'Cell phone', 'tel', { placeholder: '082 123 4567' })}
            <div className="sm:col-span-2">{inp('email', 'Email', 'email')}</div>
          </div>
          <div>
            <Label className="text-xs">Services quoted</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {SERVICES.map((s) => (
                <button type="button" key={s} onClick={() => toggle(s)} data-testid={`prospect-service-${s.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${f.services.includes(s) ? 'bg-emerald-800 text-white border-emerald-800' : 'bg-white text-stone-700 border-stone-200 hover:border-emerald-700'}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {inp('monthlyFee', 'Monthly fee (R, excl. VAT)', 'number', { min: 0, step: '50' })}
            {inp('onceOffFee', 'Once-off onboarding fee (R)', 'number', { min: 0, step: '50' })}
          </div>
          <div>
            <Label htmlFor="pr-notes" className="text-xs">Notes</Label>
            <Textarea id="pr-notes" rows={3} value={f.notes} onChange={set('notes')} className="mt-1" data-testid="prospect-notes-input"/>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="prospect-save-btn">{busy && <Loader2 size={14} className="mr-2 animate-spin"/>}{prospect ? 'Save changes' : 'Add prospect'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
